import React, { useState } from "react";
import { ActionIcon, Tooltip, Popover, Badge } from "@mantine/core";
import { AlertTriangle, CircleDotted, EyeCheck, Robot } from "tabler-icons-react";
import { AutoVerifiedData, AnomalySummary } from "@captiva/shared-types";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";

export interface VerifiedIconElement {
  companyId?: string;
  transactionReviewed?: boolean;
  autoVerified?: AutoVerifiedData;
  anomalySummary?: AnomalySummary;
  [key: string]: any;
}

export interface VerifiedIconProps {
  element: VerifiedIconElement;
  size?: number;
  anomalyDetectionEnabled?: boolean;
}

export interface ReviewData {
  checksPassed: number;
  total: number;
  passed: boolean;
  failedChecks: string;
}

// Maps static form keys to human-readable labels
function mapKeyToReadableLabel(key: string): string {
  const mapping: { [key: string]: string } = {
    // capturedTankLevelReading
    'dipReadingPhoto': 'Dip / Tank Level Reading Photo',
    'signedByImage': 'Signature',
    
    // capturedFuelTankIssue
    'odoOrHourReadingPhoto': 'Odometer / Hour Meter Reading Photo',
    'driverOperatorSignedImage': 'Driver/Operator Signature',
    'pumpAttendantSignedImage': 'Pump Attendant Signature',
    'tankMeterReadingEndPhoto': 'Tank Meter End Reading Photo',
    'tankMeterReadingStartPhoto': 'Tank Meter Start Reading Photo',
    
    // capturedRefuel
    'pumpPhoto': 'Fuel Pump Photo',
    'slipPhoto': 'Transaction Slip / Receipt Photo',
    
    // capturedBulkFuelReceived
    'dipReadingBeforePhoto': 'Dip Reading Before Delivery Photo',
    'deliveryRegistrationNumberPhoto': 'Truck Registration Plate Photo',
    'supplierPumpReadingBeforePhoto': 'Supplier Pump Meter Start Photo',
    'supplierPumpReadingAfterPhoto': 'Supplier Pump Meter End Photo',
    'dipReadingAfterPhoto': 'Dip Reading After Delivery Photo',
    'supplierDeliveryNoteNumberPhoto': 'Supplier Delivery Note Photo',
    'pumpAttendantSignatureImage': 'Pump Attendant Signature',
    'supplierSignedImage': 'Supplier Driver Signature',
    'tankSealPhotos': 'Tank Seal Photo',
    
    // capturedFuelTankTransfer
    'fuelReceiverSignedImage': 'Fuel Receiver Signature',
    'meterReadingEndPhoto': 'Flow Meter End Reading Photo',
    'meterReadingStartPhoto': 'Flow Meter Start Reading Photo',
    'mobileTankPhoto': 'Mobile Tank Photo',
    
    // capturedTripComplete / PreInspection
    'fleetOperatorSignedImage': 'Driver/Operator Signature',
    'documentingPhotos': 'Documenting Photo',
  };
  
  if (mapping[key]) return mapping[key];
  
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

export default function VerifiedIcon({
  element,
  size = 24,
  anomalyDetectionEnabled: anomalyDetectionEnabledProp,
}: VerifiedIconProps) {
  const { anomalyDetectionEnabled: anomalyFlagFromCompany } = useFeatureFlags(
    element.companyId
  );
  const isAnomalyUiEnabled =
    anomalyDetectionEnabledProp ?? anomalyFlagFromCompany;

  const hasHumanReview = !!element.transactionReviewed;
  const hasAIreview = !!element.autoVerified;
  const anomalySummary = element.anomalySummary;
  const hasAnomaly = isAnomalyUiEnabled && !!anomalySummary?.isAnomaly;

  function checkPassRate(elementVerificationObject: AutoVerifiedData): ReviewData {
    const verifiedData = elementVerificationObject.verifiedData || {};
    const portionPassed =
      Object.values(verifiedData).filter((check) => !!check).length || 0;
    const failedChecks = Object.keys(verifiedData)
      .filter((key) => !verifiedData[key])
      .join(", ");
    const total = Object.keys(verifiedData).length || 0;
    return {
      checksPassed: portionPassed,
      total,
      passed: portionPassed === total,
      failedChecks,
    };
  }

  const reviewData: ReviewData =
    hasAIreview && element.autoVerified
      ? checkPassRate(element.autoVerified)
      : { checksPassed: 0, total: 0, passed: false, failedChecks: "" };

  const [opened, setOpened] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const isCriticalOrHighAnomaly =
    hasAnomaly &&
    (anomalySummary?.severity === "CRITICAL" || anomalySummary?.severity === "HIGH");

  const renderVerificationSection = () => {
    if (!element.autoVerified) return null;
    const verifiedData = element.autoVerified.verifiedData || {};
    const reasons = element.autoVerified.reasons || {};

    if (Object.keys(verifiedData).length > 0) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #dee2e6",
              paddingBottom: "4px",
              marginBottom: "2px",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: "12px", color: "#212529" }}>
              AI Photo Verification
            </span>
            <Badge
              size="xs"
              color={reviewData.passed ? "green" : "orange"}
              variant="light"
            >
              {`${reviewData.checksPassed}/${reviewData.total} passed`}
            </Badge>
          </div>
          {Object.keys(verifiedData).map((key) => {
            const passed = !!verifiedData[key];
            const reason =
              reasons[key] || (passed ? "Passed verification" : "Failed verification");
            const label = mapKeyToReadableLabel(key);
            return (
              <div
                key={key}
                style={{ fontSize: "11px", lineHeight: "1.4", color: "#495057" }}
              >
                <span
                  style={{
                    color: passed ? "#2b8a3e" : "#e03131",
                    marginRight: "6px",
                    fontWeight: "bold",
                  }}
                >
                  {passed ? "✓" : "✗"}
                </span>
                <strong style={{ color: "#212529" }}>{label}:</strong>{" "}
                {!passed || showDetails ? (
                  <span style={{ color: passed ? "#495057" : "#c92a2a" }}>{reason}</span>
                ) : (
                  <span style={{ color: "#868e96", fontStyle: "italic" }}>Passed</span>
                )}
              </div>
            );
          })}
          <div
            style={{
              marginTop: "2px",
              borderTop: "1px solid #dee2e6",
              paddingTop: "4px",
              textAlign: "center",
            }}
          >
            <span
              style={{
                color: "#228be6",
                cursor: "pointer",
                fontSize: "10px",
                textDecoration: "underline",
                fontWeight: 500,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowDetails(!showDetails);
              }}
            >
              {showDetails ? "Hide explanations" : "Show full explanations"}
            </span>
          </div>
        </div>
      );
    }

    return (
      <span
        style={{
          whiteSpace: "pre-line",
          fontSize: "11px",
          lineHeight: "1.4",
          color: "#212529",
        }}
      >
        {`${reviewData.checksPassed}/${reviewData.total} data match - AI Review ${
          reviewData.failedChecks.length > 0 ? `\n(${reviewData.failedChecks})` : ""
        }\n*image text recognition*`}
      </span>
    );
  };

  const renderAnomalySection = () => {
    if (!hasAnomaly || !anomalySummary) return null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #dee2e6",
            paddingBottom: "6px",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: "12px", color: "#c92a2a" }}>
            AI Anomaly Detected
          </span>
          <Badge
            size="xs"
            color={isCriticalOrHighAnomaly ? "red" : "orange"}
            variant="filled"
          >
            {anomalySummary.severity}
          </Badge>
        </div>
        <div style={{ fontWeight: 600, fontSize: "12px", color: "#212529" }}>
          {anomalySummary.headline}
        </div>
        {anomalySummary.explanation && (
          <div style={{ fontSize: "11px", lineHeight: "1.45", color: "#495057" }}>
            {anomalySummary.explanation}
          </div>
        )}
        {anomalySummary.recommendedAction && (
          <div
            style={{
              fontSize: "11px",
              lineHeight: "1.4",
              backgroundColor: "#fff9db",
              border: "1px solid #ffe066",
              borderRadius: "4px",
              padding: "6px 8px",
              color: "#5c4800",
            }}
          >
            <strong>Recommended Action:</strong> {anomalySummary.recommendedAction}
          </div>
        )}
      </div>
    );
  };

  const renderHumanReviewSection = () => {
    if (!hasHumanReview) return null;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "11px",
          color: "#2b8a3e",
          fontWeight: 600,
        }}
      >
        <EyeCheck size={14} color="green" />
        <span>Human reviewed</span>
      </div>
    );
  };

  // Case 1: Human reviewed and no anomaly -> simple human review tooltip
  if (hasHumanReview && !hasAnomaly) {
    return (
      <Tooltip
        wrapLines
        opened={opened}
        openDelay={300}
        label="Human reviewed"
      >
        <ActionIcon
          aria-label="Human reviewed status"
          onClick={(e) => {
            e.stopPropagation();
            setOpened(!opened);
          }}
          onMouseOver={() => setOpened(true)}
          onMouseLeave={() => setOpened(false)}
        >
          <EyeCheck size={size} color="green" />
        </ActionIcon>
      </Tooltip>
    );
  }

  // Case 2: Neither AI review nor Anomaly -> unreviewed dotted icon
  if (!hasAIreview && !hasAnomaly) {
    return <CircleDotted size={size} color="gray" />;
  }

  // Case 3: Unified AI Verification + Anomaly Indicator & Popover
  const ariaLabel = hasAnomaly && anomalySummary
    ? `AI Anomaly Alert: ${anomalySummary.headline}${
        hasAIreview
          ? ` | AI Verification: ${reviewData.checksPassed}/${reviewData.total} checks passed`
          : ""
      }`
    : `AI Verification: ${reviewData.checksPassed}/${reviewData.total} checks passed`;

  const renderUnifiedIcon = () => {
    if (hasAnomaly) {
      return (
        <AlertTriangle
          size={size}
          color={isCriticalOrHighAnomaly ? "#e03131" : "#f08c00"}
        />
      );
    }
    return <Robot size={size} color={reviewData.passed ? "green" : "orange"} />;
  };

  return (
    <Popover
      opened={opened}
      onClose={() => setOpened(false)}
      target={
        <ActionIcon
          aria-label={ariaLabel}
          onClick={(e) => {
            e.stopPropagation();
            setOpened((o) => !o);
          }}
          onMouseEnter={() => setOpened(true)}
          onMouseLeave={() => setOpened(false)}
        >
          {renderUnifiedIcon()}
        </ActionIcon>
      }
      width={hasAnomaly ? 310 : 280}
      position="bottom"
      withArrow
      styles={{
        body: {
          backgroundColor: "#ffffff",
          color: "#212529",
          borderColor: hasAnomaly ? "#ffc9c9" : "#dee2e6",
          boxShadow: hasAnomaly
            ? "0 4px 14px rgba(224, 49, 49, 0.15)"
            : "0 4px 12px rgba(0, 0, 0, 0.1)",
          padding: "12px",
        },
        arrow: {
          backgroundColor: "#ffffff",
          borderColor: hasAnomaly ? "#ffc9c9" : "#dee2e6",
        },
      }}
    >
      <div
        style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "2px" }}
        onMouseEnter={() => setOpened(true)}
        onMouseLeave={() => setOpened(false)}
      >
        {renderHumanReviewSection()}
        {renderAnomalySection()}
        {hasAnomaly && hasAIreview && (
          <div style={{ borderTop: "1px dashed #dee2e6" }} />
        )}
        {renderVerificationSection()}
      </div>
    </Popover>
  );
}